// eslint-disable-next-line max-classes-per-file
import { type AnyType } from './any';
import { type DbtLog } from './job';

type LightdashErrorData = {
    /**
     * Optional URL linking to relevant documentation.
     * Can be used to provide users with additional context/guidance about the error.
     */
    documentationUrl?: string;
    [key: string]: AnyType;
};

type LightdashErrorParams = {
    message: string;
    name: string;
    statusCode: number;
    data: LightdashErrorData;
};

export const WARMING_UP_WAREHOUSE_ERROR_MESSAGE =
    'Warehouse is warming up and will be available soon';
export const CONNECTION_TERMINATION_ERROR_MESSAGE =
    'Connection terminated due to connection timeout';

export class LightdashError extends Error {
    statusCode: number;

    data: LightdashErrorData;

    constructor({ message, name, statusCode, data }: LightdashErrorParams) {
        super(message);
        this.name = name;
        this.statusCode = statusCode;
        this.data = data;
    }
}

export class NotExistsError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'NotExistsError',
            statusCode: 404,
            data: {},
        });
    }
}

export class ForbiddenError extends LightdashError {
    constructor(
        message = "You don't have access to this resource or action",
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'ForbiddenError',
            statusCode: 403,
            data,
        });
    }
}

export class DeactivatedAccountError extends LightdashError {
    constructor(
        message = 'Your account has been deactivated. Please contact your organization administrator.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'DeactivatedAccountError',
            statusCode: 403,
            data,
        });
    }
}

export class BillingInactiveError extends LightdashError {
    constructor(
        message = 'Your account billing is inactive. Please contact your organization administrator.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'BillingInactiveError',
            statusCode: 402,
            data,
        });
    }
}

export class AuthorizationError extends LightdashError {
    constructor(
        message = "You don't have authorization to perform this action",
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'AuthorizationError',
            statusCode: 401,
            data,
        });
    }
}

export class AxiosError extends LightdashError {
    public errorMessage: string;

    constructor(message: string, errorMessage: string) {
        super({
            message,
            name: 'Request failed',
            statusCode: 400,
            data: {},
        });
        this.errorMessage = errorMessage;
    }
}

export class FynoError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'Request failed',
            statusCode: 400,
            data: {},
        });
    }
}

export class ExpiredError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'ExpiredError',
            statusCode: 406,
            data: {},
        });
    }
}

export class ParameterError extends LightdashError {
    constructor(
        message: string = 'Incorrect parameters',
        data: Record<string, AnyType> = {},
    ) {
        super({
            message,
            name: 'ParameterError',
            statusCode: 400,
            data,
        });
    }
}

export class NonCompiledModelError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType } = {}) {
        super({
            message,
            name: 'NonCompiledModelError',
            statusCode: 400,
            data,
        });
    }
}

export class MissingCatalogEntryError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType }) {
        super({
            message,
            name: 'MissingCatalogEntryError',
            statusCode: 400,
            data,
        });
    }
}

export class MissingWarehouseCredentialsError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'MissingWarehouseCredentialsError',
            statusCode: 400,
            data: {},
        });
    }
}

export class MissingBlobStorageCredentialsError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'MissingBlobStorageCredentialsError',
            statusCode: 400,
            data: {},
        });
    }
}

export class UnexpectedServerError extends LightdashError {
    constructor(
        message = 'Something went wrong.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'UnexpectedServerError',
            statusCode: 500,
            data,
        });
    }
}
export class UnexpectedIndexError extends LightdashError {
    constructor(
        message = 'Invalid index in array.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'UnexpectedIndexError',
            statusCode: 500,
            data,
        });
    }
}
export class UnexpectedGitError extends LightdashError {
    constructor(
        message = 'Unexpected error in Git adapter',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'UnexpectedGitError',
            statusCode: 400,
            data,
        });
    }
}

export class InvalidRequest extends LightdashError {
    constructor(
        message = 'Invalid request',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'InvalidRequest',
            statusCode: 400,
            data,
        });
    }
}

export class UnexpectedDatabaseError extends LightdashError {
    constructor(
        message = 'Unexpected error in Lightdash database.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'UnexpectedDatabaseError',
            statusCode: 500,
            data,
        });
    }
}

export class ParseError extends LightdashError {
    constructor(
        message = 'Error parsing dbt project and lightdash metadata',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'ParseError',
            statusCode: 400,
            data,
        });
    }
}

export class CompileError extends LightdashError {
    constructor(
        message = 'Error compiling sql from Lightdash configuration',
        data: Record<string, AnyType> = {},
    ) {
        super({
            message,
            name: 'CompileError',
            statusCode: 400,
            data,
        });
    }
}

export class FieldReferenceError extends LightdashError {
    constructor(
        message = 'Failed to reference field in dbt project',
        data: Record<string, AnyType> = {},
    ) {
        super({
            message,
            name: 'FieldReferenceError',
            statusCode: 400,
            data,
        });
    }
}

export class DbtError extends LightdashError {
    logs: DbtLog[] | undefined;

    constructor(message = 'Dbt raised an error', logs: DbtLog[] = []) {
        super({
            message,
            name: 'DbtError',
            statusCode: 400,
            data: {},
        });
        this.logs = logs;
    }
}

export class NotFoundError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'NotFoundError',
            statusCode: 404,
            data: {},
        });
    }
}

export class NotSupportedError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'NotSupportedError',
            statusCode: 400,
            data: {},
        });
    }
}

export class InvalidUser extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'InvalidUser',
            statusCode: 404,
            data: {},
        });
    }
}

export class WarehouseConnectionError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'WarehouseConnectionError',
            statusCode: 400,
            data: {},
        });
    }
}

export class WarehouseQueryError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType } = {}) {
        super({
            message,
            name: 'WarehouseQueryError',
            statusCode: 400,
            data,
        });
    }
}

export class SmptError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType } = {}) {
        super({
            message,
            name: 'SmptError',
            statusCode: 500,
            data,
        });
    }
}

export class SendgridError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType } = {}) {
        super({
            message,
            name: 'SendgridError',
            statusCode: 500,
            data,
        });
    }
}

export class AlreadyProcessingError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AlreadyProcessingError',
            statusCode: 409,
            data: {},
        });
    }
}
export class AlreadyExistsError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AlreadyExistsError',
            statusCode: 409,
            data: {},
        });
    }
}

export class CommunicationError extends LightdashError {
    constructor(message: string, statusCode: number) {
        super({
            message,
            name: 'CommunicationError',
            statusCode,
            data: {},
        });
    }
}
export class MissingConfigError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'MissingConfigError',
            statusCode: 422,
            data: {},
        });
    }
}

export class MapperError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'MapperError',
            statusCode: 400,
            data: {},
        });
    }
}

export class NotEnoughResults extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'NotEnoughResults',
            statusCode: 406,
            data: {},
        });
    }
}

export class PaginationError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'PaginationError',
            statusCode: 422,
            data: {},
        });
    }
}

export class EventTableError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'EventTableError',
            statusCode: 400,
            data: {},
        });
    }
}
export class SlackInstallationNotFoundError extends LightdashError {
    constructor(message: string = 'Could not find slack installation') {
        super({
            message,
            name: 'SlackInstallationNotFoundError',
            statusCode: 404,
            data: {},
        });
    }
}

export class AuthenticationError extends LightdashError {
    constructor(
        message = "You don't have valid credentials to perform this action",
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'AuthenticationError',
            statusCode: 401,
            data,
        });
    }
}
export class SlackError extends LightdashError {
    constructor(
        message: string = 'Slack API error occurred',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'SlackError',
            statusCode: 400,
            data,
        });
    }
}

export class ValidationError extends LightdashError {
    constructor(
        message = 'Validation Error',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'ValidationError',
            statusCode: 400,
            data,
        });
    }
}

export class CampaignValidationError extends LightdashError {
    constructor(
        message = 'Campaign validation failed',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'CampaignValidationError',
            statusCode: 400,
            data,
        });
    }
}

export class SlackFileUploadError extends LightdashError {
    constructor(
        message: string = 'Slack file upload failed',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'SlackFileUploadError',
            statusCode: 400,
            data,
        });
    }
}

export class MsTeamsError extends LightdashError {
    constructor(
        message: string = 'Microsoft Teams API error occurred',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'MsTeamsError',
            statusCode: 400,
            data,
        });
    }
}

export class UnexpectedGoogleSheetsError extends LightdashError {
    constructor(
        message = 'Unexpected error in Google sheets client',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'UnexpectedGoogleSheetsError',
            statusCode: 400,
            data,
        });
    }
}

export class WarehouseError extends LightdashError {
    constructor(
        message = 'Databricks Error',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'WarehouseError',
            statusCode: 401,
            data,
        });
    }
}

/* This specific error will be used in the frontend
to show a "Re-authenticate" button in the UI for Google Sheets scope issues
*/
export class GoogleSheetsScopeError extends LightdashError {
    constructor(
        message = 'Unable to validate Google Sheets file. Please re-authenticate with Google to grant the required permissions.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'GoogleSheetsScopeError',
            statusCode: 403,
            data,
        });
    }
}

export class GoogleSheetsTransientError extends LightdashError {
    constructor(
        message = 'Unexpected error in Google Sheets API',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'GoogleSheetsTransientError',
            statusCode: 500,
            data,
        });
    }
}

export class GoogleSheetsQuotaError extends LightdashError {
    constructor(
        message = 'Google Sheets API quota exceeded. The sync will be retried automatically.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'GoogleSheetsQuotaError',
            statusCode: 429,
            data,
        });
    }
}
export class NotImplementedError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'Not implemented error',
            statusCode: 500,
            data: [],
        });
    }
}

export class NullFieldError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'NullFieldError',
            statusCode: 400,
            data: {},
        });
    }
}

export class DuplicateEntriesError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'DuplicateEntriesError',
            statusCode: 400,
            data: {},
        });
    }
}

export const getErrorMessage = (e: unknown) =>
    e instanceof Error ? e.message : `Unknown ${typeof e} error`;

export const getErrorCode = (e: unknown) =>
    e instanceof Error && 'code' in e
        ? (e as { code: unknown }).code
        : undefined;

export class ScreenshotError extends LightdashError {
    constructor(
        message = 'Error capturing screenshot',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'ScreenshotError',
            statusCode: 500,
            data,
        });
    }
}

export class SshTunnelError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'SshTunnelError',
            statusCode: 400,
            data: {},
        });
    }
}

export class ReadFileError extends LightdashError {
    constructor(message: string, data: { [key: string]: AnyType } = {}) {
        super({
            message,
            name: 'ReadFileError',
            statusCode: 404,
            data,
        });
    }
}

export class S3Error extends LightdashError {
    constructor(
        message = 'Error occurred while interacting with S3',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'S3Error',
            statusCode: 500,
            data,
        });
    }
}

export class ResultsExpiredError extends LightdashError {
    constructor(
        message = 'Your results have expired. Please refresh the page or re-run the query.',
    ) {
        super({
            message,
            name: 'ResultsExpiredError',
            statusCode: 404,
            data: {},
        });
    }
}

export class TimeoutError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'TimeoutError',
            statusCode: 400,
            data: {},
        });
    }
}

export class OpenAIError extends LightdashError {
    constructor(
        message = 'OpenAI service is temporarily unavailable. Please try again after some time.',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'OpenAIError',
            statusCode: 503,
            data,
        });
    }
}

export class AiDuplicateSlackPromptError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AiDuplicateSlackPromptError',
            statusCode: 400,
            data: {},
        });
    }
}

export class AiSlackMappingNotFoundError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AiSlackMappingNotFoundError',
            statusCode: 400,
            data: {},
        });
    }
}

export class AiAgentNotFoundError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AiAgentNotFoundError',
            statusCode: 400,
            data: {},
        });
    }
}

export class AiAgentValidatorError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'AiAgentValidatorError',
            statusCode: 400,
            data: {},
        });
    }
}

/* This specific error will be used in the frontend
to show a "reauthenticate" button in the UI
*/
export class SnowflakeTokenError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'SnowflakeTokenError',
            statusCode: 401,
            data: {},
        });
    }
}

export class CustomSqlQueryForbiddenError extends LightdashError {
    constructor(
        message: string = 'User cannot run queries with custom SQL dimensions',
    ) {
        super({
            message,
            name: 'CustomSqlQueryForbiddenError',
            statusCode: 403,
            data: {
                documentationUrl: `https://docs.lightdash.com/references/custom-fields#custom-sql`,
            },
        });
    }
}

export class OauthAuthenticationError extends LightdashError {
    constructor(
        message = 'OAuth authentication error',
        data: { [key: string]: AnyType } = {},
    ) {
        super({
            message,
            name: 'OauthAuthenticationError',
            statusCode: 401,
            data,
        });
    }
}

export class GenerateDailySchedulerJobError extends LightdashError {
    schedulerUuid: string;

    constructor(
        message: string,
        schedulerUuid: string,
        originalError?: unknown,
    ) {
        super({
            message,
            name: 'GenerateDailySchedulerJobError',
            statusCode: 500,
            data: {
                schedulerUuid,
                originalError:
                    originalError instanceof Error
                        ? originalError.message
                        : String(originalError),
            },
        });
        this.schedulerUuid = schedulerUuid;
        if (originalError instanceof Error) {
            this.stack = originalError.stack;
            this.cause = originalError;
        }
    }
}

export class LightdashProjectConfigError extends LightdashError {
    constructor(
        message = 'Invalid lightdash.config.yml',
        data: Record<string, AnyType> = {},
    ) {
        super({
            message,
            name: 'LightdashProjectConfigError',
            statusCode: 400,
            data,
        });
    }
}

export class CorruptedExploreError extends LightdashError {
    constructor(
        message = 'Explore has corrupted or missing data',
        data: Record<string, AnyType> = {},
    ) {
        super({
            message,
            name: 'CorruptedExploreError',
            statusCode: 500,
            data,
        });
    }
}

export class ChangesetConflictError extends LightdashError {
    constructor(
        message: string,
        data: {
            entityName: string;
            entityTableName: string;
        },
    ) {
        super({
            message,
            name: 'ChangesetConflictError',
            statusCode: 409,
            data,
        });
    }
}

export class InvalidSpaceStateError extends LightdashError {
    constructor(message: string) {
        super({
            message,
            name: 'InvalidSpaceStateError',
            statusCode: 500,
            data: {},
        });
    }
}
